export const POSITION_CONVENTION = "gravity-position-v1-rx-neg90-projection-0.1-nearest-centre";
export type AngularSmoothing = 0 | 5 | 15 | 30;

/** Integral of a centred box over integer-centred bins: total width, fractional edges. */
export function smoothingKernel(width: AngularSmoothing): readonly [number, number][] {
  if (!width) return [[0, 1]];
  const half = width / 2;
  const weights: [number, number][] = [];
  for (let offset = -Math.ceil(half); offset <= Math.ceil(half); offset++) {
    const overlap = Math.max(0, Math.min(offset + .5, half) - Math.max(offset - .5, -half));
    if (overlap) weights.push([offset, overlap / width]);
  }
  return weights;
}

export function smoothPositionBins(raw: readonly number[], width: AngularSmoothing): readonly number[] {
  if (raw.length !== 360 || raw.some((v) => !Number.isFinite(v) || v < 0)) throw new TypeError("Expected 360 finite duration bins");
  if (!width) return raw;
  const kernel = smoothingKernel(width);
  return raw.map((_, degree) => kernel.reduce((sum, [offset, weight]) => sum + raw[(degree + offset + 360) % 360] * weight, 0));
}

export function positionHistogramPath(bins: readonly number[], maximum: number): string {
  if (!maximum) return "";
  return bins.map((v, degree) => {
    const radius = 100 * v / maximum;
    const theta = degree * Math.PI / 180;
    return `${degree ? "L" : "M"}${(180 + radius * Math.sin(theta)).toFixed(3)} ${(150 - radius * Math.cos(theta)).toFixed(3)}`;
  }).join("") + "Z";
}

export type DistributionTimeMode = "cumulative" | "interval";
export type DistributionColourMode = "sensor" | "spo2";
import type { MetricsPositionDistribution, MetricsPositionSensor } from "@rekonas/domain";

export function distributionSnapshot(summary: MetricsPositionDistribution, time: number, mode: DistributionTimeMode) {
  const { duration_seconds: duration } = summary.identity;
  const count = Math.ceil(duration / summary.window_s);
  const cursor = Math.max(0, Math.min(duration, time));
  const index = cursor === duration ? count - 1 : mode === "interval" ? Math.floor(cursor / summary.window_s) : Math.floor(cursor / summary.window_s) - 1;
  return { index, start: mode === "interval" && index >= 0 ? index * summary.window_s : 0,
    end: index < 0 ? 0 : Math.min((index + 1) * summary.window_s, duration) };
}

export interface DistributionBins { seconds: readonly number[]; sums: readonly number[] | null; counts: readonly number[] | null }
const zeros = Object.freeze(Array<number>(360).fill(0));
function accumulation(rows: number[][] | null, index: number, interval: boolean): readonly number[] | null {
  if (rows === null) return null;
  if (index < 0) return zeros;
  return interval && index ? rows[index].map((value, bin) => value - rows[index - 1][bin]) : rows[index];
}
export function distributionBins(sensor: MetricsPositionSensor | null, index: number, mode: DistributionTimeMode, smoothing: AngularSmoothing): DistributionBins | null {
  if (!sensor) return null;
  const smooth = (rows: number[][] | null) => {
    const values = accumulation(rows, index, mode === "interval");
    return values === null ? null : smoothPositionBins(values, smoothing);
  };
  return { seconds: smooth(sensor.seconds)!, sums: smooth(sensor.spo2_sums), counts: smooth(sensor.spo2_counts) };
}

/** One common scale for both sensors, stable for every snapshot in this mode. */
export function distributionMaximum(summary: MetricsPositionDistribution, mode: DistributionTimeMode, smoothing: AngularSmoothing): number {
  let maximum = 0;
  for (const sensor of Object.values(summary.sensors)) {
    if (!sensor) continue;
    const indices = mode === "cumulative" ? [sensor.seconds.length - 1] : sensor.seconds.map((_, index) => index);
    for (const index of indices) {
      // Duration scale never depends on SpO2 availability/colour mode.
      const values = accumulation(sensor.seconds, index, mode === "interval")!;
      for (const value of smoothPositionBins(values, smoothing)) maximum = Math.max(maximum, value);
    }
  }
  return maximum ? Math.ceil(maximum / (maximum >= 120 ? 60 : 1)) * (maximum >= 120 ? 60 : 1) : 0;
}

export const SPO2_COLOUR_RANGE = [70, 100] as const;
export const SPO2_NO_DATA_COLOUR = "#9aabb7";
/** Shared fixed measured-% scale; values are not clinical risk categories. */
export function spo2Colour(sum: number | null, count: number | null): string {
  if (sum === null || count === null || count <= 0) return SPO2_NO_DATA_COLOUR;
  const t = Math.max(0, Math.min(1, (sum / count - SPO2_COLOUR_RANGE[0]) / (SPO2_COLOUR_RANGE[1] - SPO2_COLOUR_RANGE[0])));
  // Continuous purple -> teal, independently legible from outline/dash identity.
  return `rgb(${Math.round(111 - 76 * t)}, ${Math.round(70 + 115 * t)}, ${Math.round(164 - 16 * t)})`;
}
export function distributionBinPath(bin: number, seconds: number, maximum: number, width: number): string {
  if (maximum === 0 || seconds === 0) return "";
  const point = (degrees: number) => {
    const angle = degrees * Math.PI / 180, radius = 100 * seconds / maximum;
    return `${(180 + radius * Math.sin(angle)).toFixed(3)} ${(150 - radius * Math.cos(angle)).toFixed(3)}`;
  };
  return `M180 150L${point(bin * width)}L${point((bin + 1) * width)}Z`;
}
